import { ThrottleConfig } from 'hooks/useThrottleConfig'
import { Device } from 'hooks/useDevices'
import { hidSend } from 'connections/hid';
import { serializeSetUsbThrottleControlRequest } from 'utils/common-types';

export type RunBISTParams = {
  connectedDevice: Device | undefined
  actuatorIndex?: number
  throttleConfig: ThrottleConfig | undefined
  currentPosition?: number
}

export async function runBISTPositionCommand({
  connectedDevice,
  actuatorIndex,
  throttleConfig,
  currentPosition
}: RunBISTParams) {
  if (!throttleConfig || throttleConfig.invalidReason !== undefined) return
  const arrivedDeadbandUserUnits = throttleConfig.positionControlDeadband
  const positionResolution = throttleConfig.positionResolution
  const resolutionFactor = Math.pow(
    10,
    positionResolution === undefined || positionResolution === 0 ? 0 : positionResolution
  )
  const minPosition = throttleConfig.positionControlMinPos / resolutionFactor
  const maxPosition = throttleConfig.positionControlMaxPos / resolutionFactor
  const centerPosition = throttleConfig.positionControlHomePos / resolutionFactor
  // Returns true if send was attempted (device attached), false if device is detached (to break out of loop)
  const send = (userVal: number) => {
    if (!connectedDevice || (connectedDevice.initialized && !connectedDevice.attached)) return false
    const wireValue = Math.round(userVal * resolutionFactor)
    hidSend(
      connectedDevice.handle,
      serializeSetUsbThrottleControlRequest({
        value: wireValue,
        motorIndex: actuatorIndex ?? 0
      })
    ).catch((e: unknown) => console.warn('Failed to send BIST position command:', e))
    return true
  }
  const POLL_INTERVAL_MS = 50
  const ARRIVAL_TIMEOUT_MS = 30000
  const POST_ARRIVAL_DWELL_MS = 400

  const moveAndWait = async (targetUser: number) => {
    const start = Date.now()
    if (!send(targetUser)) return
    while (Date.now() - start < ARRIVAL_TIMEOUT_MS) {
      if (!connectedDevice || (connectedDevice.initialized && !connectedDevice.attached)) return
      if (currentPosition !== undefined && Math.abs(currentPosition - targetUser) <= arrivedDeadbandUserUnits) {
        return
      }
      if (!send(targetUser)) return
      await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS))
    }
  }

  await moveAndWait(minPosition)
  await new Promise((res) => setTimeout(res, POST_ARRIVAL_DWELL_MS))
  await moveAndWait(maxPosition)
  await new Promise((res) => setTimeout(res, POST_ARRIVAL_DWELL_MS))
  await moveAndWait(centerPosition)
}
export {}
// Utility for sending center position command for Position widget
// Extracted from Position.tsx moveToCenter callback

export type SendCenterPositionCommandParams = {
  connectedDevice: Device | undefined
  actuatorIndex?: number
  center: number
  resolution: number
  minPosition: number
  maxPosition: number
  currentPosition?: number
}

const PULSE_INTERVAL_MS = 50
// Fraction of the travel range used as the "arrived" deadband for center moves.
const ARRIVED_DEADBAND_RANGE_FRACTION = 0.02

export async function sendCenterPositionCommand({
  connectedDevice,
  actuatorIndex,
  center,
  resolution,
  minPosition,
  maxPosition,
  currentPosition,
}: SendCenterPositionCommandParams) {
  // Returns true if send was attempted (device attached), false if device is detached (to break out of loop)
  const sendCenter = () => {
    if (!connectedDevice || (connectedDevice.initialized && !connectedDevice.attached)) return false
    hidSend(
      connectedDevice.handle,
      serializeSetUsbThrottleControlRequest({
        value: Math.round(center * resolution),
        motorIndex: actuatorIndex ?? 0
      })
    ).catch((e: unknown) => console.warn('Failed to send position command:', e))
    return true
  }

  const range = Math.max(0.001, Math.abs(maxPosition - minPosition))
  const arrivedDeadbandUserUnits = range * ARRIVED_DEADBAND_RANGE_FRACTION
  const ARRIVAL_TIMEOUT_MS = 5000

  const start = Date.now()
  if (!sendCenter()) return
  while (Date.now() - start < ARRIVAL_TIMEOUT_MS) {
    if (!connectedDevice || (connectedDevice.initialized && !connectedDevice.attached)) return
    if (currentPosition !== undefined && Math.abs(currentPosition - center) <= arrivedDeadbandUserUnits) {
      return
    }
    // Keep sending while moving so firmware continues to honor the target.
    if (!sendCenter()) return
    await new Promise((res) => setTimeout(res, PULSE_INTERVAL_MS))
  }
}
